import React, { useState } from 'react'
import { X, Image, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import GalleryImageSelector from '@/features/social/components/gallery/GalleryImageSelector'

interface PostMediaUploaderProps {
  imageUrls: string[]
  onImagesChange: (urls: string[]) => void
  platform?: string
  error?: string | null
  isSubmitting: boolean
  /** Called when a gallery is selected, for Caption Agent context */
  onGallerySelect?: (galleryId: number) => void
}

export function PostMediaUploader({
  imageUrls,
  onImagesChange,
  platform,
  error,
  isSubmitting,
  onGallerySelect
}: PostMediaUploaderProps) {
  const { error: toastError } = useToast()
  const [showGallerySelector, setShowGallerySelector] = useState(false)

  const handleImagesSelected = (newImages: string[]) => {
    const uniqueNewImages = newImages.filter(url => !imageUrls.includes(url))
    const updatedImages = [...imageUrls, ...uniqueNewImages]

    // Check Instagram limit
    if (platform === 'INSTAGRAM' && updatedImages.length > 10) {
      toastError('Image Limit', 'Instagram allows a maximum of 10 images per post')
      return
    }

    onImagesChange(updatedImages)
  }

  const removeImage = (urlToRemove: string) => {
    onImagesChange(imageUrls.filter(url => url !== urlToRemove))
  }

  const reorderImages = (startIndex: number, endIndex: number) => {
    const newImageUrls = [...imageUrls]
    const [reorderedItem] = newImageUrls.splice(startIndex, 1)
    newImageUrls.splice(endIndex, 0, reorderedItem)
    onImagesChange(newImageUrls)
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Don't start drag if clicking on button elements
    const target = e.target as HTMLElement
    if (target.closest('button')) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    if (dragIndex !== dropIndex) {
      reorderImages(dragIndex, dropIndex)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-muted-foreground">
          Upload or select images
        </Label>
        {platform === 'INSTAGRAM' && (
          <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded-full">
            {imageUrls.length} / 10
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
        {imageUrls.map((url, index) => (
          <div
            key={url} // Changed key from index to url for better React reconciliation
            className="relative group cursor-move aspect-square"
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="relative w-full h-full rounded-xl overflow-hidden border border-border shadow-sm group-hover:ring-2 ring-primary/50 transition-all">
              <img
                src={url}
                alt={`Image ${index + 1}`}
                className="w-full h-full object-cover"
              />

              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

              <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm text-white text-2xs font-bold px-1.5 py-0.5 rounded-md">
                {index + 1}
              </div>

              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute top-1.5 right-1.5 h-6 w-6 opacity-0 group-hover:opacity-100 transition-all scale-90 group-hover:scale-100 shadow-sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  removeImage(url)
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className={cn(
            "aspect-square flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-primary/5 transition-all group",
            (platform === 'INSTAGRAM' && imageUrls.length >= 10) && "opacity-50 cursor-not-allowed hover:border-muted-foreground/25 hover:bg-transparent"
          )}
          onClick={() => setShowGallerySelector(true)}
          disabled={isSubmitting || (platform === 'INSTAGRAM' && imageUrls.length >= 10)}
        >
          <div className="h-10 w-10 rounded-full bg-secondary group-hover:bg-white flex items-center justify-center mb-2 transition-colors shadow-sm">
            <Image className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <span className="text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors">Add Image</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-1 text-sm text-red-500 bg-red-50 p-2 rounded-md">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {showGallerySelector && (
        <GalleryImageSelector
          onImagesSelected={handleImagesSelected}
          onClose={() => setShowGallerySelector(false)}
          maxImages={10}
          onGallerySelect={onGallerySelect}
        />
      )}
    </div>
  )
}

